import React, { useState } from "react";
import AspectRatio from "@mui/joy/AspectRatio";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import { Grid, Chip } from "@mui/material";
import BookmarkAdd from "@mui/icons-material/BookmarkAddOutlined";
import Bookmark from "@mui/icons-material/Bookmark";

import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import styles from "./AllListings.module.css";
import { useAuth } from "../../../contexts/AuthContext";

import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";

const Listing = ({ listing, handleBookmarkToggle }) => {
  const { accessToken, showLoginModal } = useAuth();
  // console.log("Listings", listing);
  const [isBookmarked, setIsBookmarked] = useState(listing.isFavorite || false);
  const navigate = useNavigate();

  function formatMoney(number) {
    if (typeof number === "number") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(number);
    }
    return number;
  }

  const handleCardClick = (listingUrl, isMLS) => {
    window.open(`/edit-listing/${listingUrl}`, "_blank");
  };

  const handleBookmarkClick = async (e, listingId) => {
    e.stopPropagation(); // Prevent card click event
    const newBookmarkState = !isBookmarked;

    try {
      await handleBookmarkToggle(listingId, isBookmarked);
      setIsBookmarked(newBookmarkState); // Update UI on success
    } catch (error) {
      console.error("Error toggling bookmark:", error);
    }
  };

  // const isPremium = listing.is_premium === true && listing.landlord_id !== null;

  const isPremium = listing.is_premium === true;

  const handleContactLandlord = () => {
    // e.preventDefault();
    // check if the user is logged in
    if (!accessToken) {
      // user is not logged in
      // prompt login/signup
      showLoginModal();

      // @TODO: check redirect
    }

    // navigate to dash chat
    // console.log("navigate to chat");
    navigate(`/MyFirstLand?category=messages`, {
      state: {
        targetUser: {
          id: listing.partner_user_id,
          first_name: listing.partner_first_name,
          last_name: listing.partner_last_name,
          photo_url: listing.partner_photo_url,
          tags: "Realtor, Broker",
        },
        targetMessage: `Hello ${listing.partner_first_name}, I'm interested in your property: ${listing.title}.`,
      },
    });
  };

  return (
    <Card
      className={`${styles.listingContainer} ${
        isPremium ? styles.premiumCard : ""
      }`}
      variant="outlined"
      onClick={() => handleCardClick(listing.url, listing.isMLS)}
    >
      {/* Premium Badge */}
      {isPremium && <div className={styles.premiumBadge}>Premium</div>}
      {listing.isMLS && <div className={styles.mlsBadge}>MLS</div>}
      {listing.pro_id && (
        <span className={isPremium ? styles.proPremiumTag : styles.proTag}>
          Pro
        </span>
      )}
      <AspectRatio minHeight="120px" maxHeight="200px">
        <img
          src={listing.image_url}
          loading="lazy"
          alt={listing.address}
          className={styles.listingImage}
        />
      </AspectRatio>

      <CardContent>
        {/* Title */}
        <Typography level="h5" className={styles.listingTitle}>
          {listing.title}
        </Typography>

        {/* Listing Type */}
        <Typography level="body2" className={styles.listingInfo}>
          {listing.listing_type === "1" ? "For Rent" : "For Sale"}
        </Typography>

        {/* Area, Year Built */}
        <Typography level="body2" className={styles.listingInfo}>
          {listing.area_sqft} sqft | Built in {listing.year_built}
        </Typography>

        <Typography level="body2" className={styles.listingInfo}>
          <Chip icon={<BedIcon />} label={`${listing.bedrooms} Beds`} />
          <Chip icon={<BathtubIcon />} label={`${listing.bathrooms} Baths`} />
        </Typography>

        {/* Address */}
        {!listing.hide_address && (
          <Typography level="body2" className={styles.listingAddress}>
            {listing.street},{listing.street_2} {listing.city}, {listing.state},{" "}
            {listing.zipcode}
          </Typography>
        )}

        {/* Price */}
        <Typography level="body2" className={styles.listingPrice}>
          {listing.rent_price
            ? formatMoney(listing.rent_price) + " /mo"
            : listing.sell_price
            ? formatMoney(listing.sell_price)
            : "Price Not Available"}
        </Typography>

        {listing.isMLS && (
          <Typography level="body2" className={styles.mlsSource}>
            Source: MLS
          </Typography>
        )}

        {/* Additional Details for Premium Properties */}
        {isPremium && (
          <>
            <Typography level="body2" className={styles.additionalInfo}>
              Available From:{" "}
              {new Date(listing.available_from).toLocaleDateString()}
            </Typography>
            <Typography level="body2" className={styles.additionalInfo}>
              Landmark: {listing.landmark}
            </Typography>
            <Typography level="body2" className={styles.description}>
              {listing.description}
            </Typography>
            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleContactLandlord();
                }}
              >
                Contact Landlord
              </Button>
              <Button variant="outlined" color="secondary">
                Schedule a Visit
              </Button>
            </div>
          </>
        )}
      </CardContent>
      {!listing.isMLS ? (
        <div className={styles.bookmarkBadge}>
          <IconButton
            aria-label="bookmark"
            variant="plain"
            color="neutral"
            size="sm"
            className={styles.bookmarkButton}
            onClick={(e) => handleBookmarkClick(e, listing.id)}
          >
            {isBookmarked ? <Bookmark /> : <BookmarkAdd />}
          </IconButton>
        </div>
      ) : (
        ""
      )}
    </Card>
  );
};

export default Listing;
